/**
 * Who is this person, and what may they open?
 *
 * A SamVision account is not one role. The same human can own a personal
 * workspace at home, run a company, and be on someone else's payroll — three
 * contexts, three different sets of tabs. This module turns three API answers
 * into that list.
 *
 * The rule that must never bend: a personnel record grants the EMPLOYEE context
 * and nothing else. Being on the payroll is not permission to see the payroll.
 * Administration comes only from a Casbin policy in the company's domain (or from
 * owning the company outright) — never from appearing in its staff list.
 *
 * Wire shapes are the ones the live API returns (verified against
 * api.sam-vision.uz), which is NOT what the plan assumed:
 *   GET /companies              → {data: CompanyInfo[], meta} — snake_case
 *   GET /permissions/me?domain= → {permissions: [{resource, action}]} — REQUIRES a
 *                                 domain (400 without), and has no notion of roles
 *   GET /me/memberships         → {memberships: [{company_id, record_id, ...}]}
 */
import {request, requestMe} from '@/shared/api';
import {fetchMyPermissions} from '@/entities/permission/api';
import {companyDomain} from '@/entities/permission/domain';

/** Re-exported: this module used to own it, and its callers read better for it. */
export {companyDomain};

/** sv_organization CompanyInfo, as serialized by the gateway. */
export type Company = {
  id: string;
  name: string;
  /** "org" | "personal". */
  kind: string;
  owner_account_id: string;
  organization_type?: string;
};

/** One allowed action, as sv_permissions reports it for a single Casbin domain. */
export type Permission = {
  resource: string;
  action: string;
};

/** sv_control PersonnelMembershipRecord — a company where I am on the payroll. */
export type Membership = {
  company_id: string;
  record_id: string;
  full_name: string;
  position: string;
  department_names: string[];
};

export type AppContext =
  | {kind: 'home'; companyId: string; title: string}
  | {kind: 'company'; companyId: string; title: string; role: string}
  | {kind: 'employee'; companyId: string; recordId: string; title: string};

export type ResolveInput = {
  /** The caller's own account id (JWT `sub`, echoed in the login response). */
  accountId: string;
  companies: Company[];
  /** Effective permissions keyed by Casbin domain — "company:{id}". */
  permissionsByDomain: Record<string, Permission[]>;
  memberships: Membership[];
};

/**
 * Writes that only an administrator of a company can hold. `read` is deliberately
 * absent: a plain member may be granted a read, and a read must not unlock the
 * management tabs.
 */
const ADMIN_WRITES: ReadonlyArray<Permission> = [
  {resource: 'companies', action: 'update'},
  {resource: 'members', action: 'create'},
  {resource: 'personnel', action: 'create'},
  {resource: 'devices', action: 'create'},
  {resource: 'departments', action: 'create'},
];

function administers(permissions: Permission[] | undefined): boolean {
  if (!permissions?.length) {
    return false;
  }
  return permissions.some(p =>
    ADMIN_WRITES.some(a => a.resource === p.resource && a.action === p.action),
  );
}

/** Pure: no network. Everything the app needs to know about the session. */
export function resolveContexts(input: ResolveInput): AppContext[] {
  const {accountId, companies, permissionsByDomain, memberships} = input;
  const contexts: AppContext[] = [];

  for (const c of companies) {
    const owned = c.owner_account_id === accountId;

    if (c.kind === 'personal') {
      // A personal workspace belongs to exactly one account. If it is not mine,
      // it is not a Home of mine.
      if (owned) {
        contexts.push({kind: 'home', companyId: c.id, title: c.name});
      }
      continue;
    }

    if (owned) {
      contexts.push({kind: 'company', companyId: c.id, title: c.name, role: 'owner'});
    } else if (administers(permissionsByDomain[companyDomain(c.id)])) {
      contexts.push({kind: 'company', companyId: c.id, title: c.name, role: 'admin'});
    }
    // Otherwise: a company I merely belong to. Any access I have comes from a
    // personnel record below, never from this list.
  }

  for (const m of memberships) {
    contexts.push({
      kind: 'employee',
      companyId: m.company_id,
      recordId: m.record_id,
      title: m.full_name,
    });
  }

  return contexts;
}

type CompanyListResponse = {data?: Company[]; meta?: {total_pages?: number}};
type MembershipsResponse = {memberships?: Membership[]};

/**
 * Every company the caller can see, across ALL pages.
 *
 * `/companies` is paginated. For an ordinary user it answers with only the
 * companies they belong to — one short page — but a platform admin gets the
 * global list spread over many pages, and a company the caller OWNS can land on
 * any of them. Reading just the first page silently dropped that company and
 * left its owner staring at onboarding ("create an organization") once the
 * platform grew past one page. So walk every page.
 *
 * (A dedicated `/me/companies` — always the caller's own companies, never the
 * global list — would be cheaper for platform admins and is the proper fix; it
 * needs a gateway change the release contour can't take until the SSO
 * perekatka. Until then this is correct on every gateway, old and new.)
 */
async function fetchAllCompanies(): Promise<Company[]> {
  const perPage = 100;
  const first = await request<CompanyListResponse>(
    `/companies?per_page=${perPage}&page=1`,
  );
  const all = first.data ?? [];
  const totalPages = first.meta?.total_pages ?? 1;
  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({length: totalPages - 1}, (_, i) =>
        request<CompanyListResponse>(`/companies?per_page=${perPage}&page=${i + 2}`),
      ),
    );
    for (const r of rest) {
      all.push(...(r.data ?? []));
    }
  }
  return all;
}

/** The three calls, then the pure resolver. */
export async function fetchContexts(accountId: string): Promise<AppContext[]> {
  const [companies, membershipList] = await Promise.all([
    fetchAllCompanies(),
    // /me/* takes the caller's identity from the JWT: no X-Company-Id, or we would
    // be filtering the answer down to a company the user never picked.
    requestMe<MembershipsResponse>('/me/memberships'),
  ]);

  const memberships = membershipList.memberships ?? [];

  // /permissions/me answers for ONE domain at a time, so ask per company. Skip the
  // ones we own — ownership already decides those, and skip personal workspaces.
  const needsCheck = companies.filter(
    c => c.kind !== 'personal' && c.owner_account_id !== accountId,
  );
  const permissionsByDomain: Record<string, Permission[]> = {};
  await Promise.all(
    needsCheck.map(async c => {
      const domain = companyDomain(c.id);
      permissionsByDomain[domain] = await fetchMyPermissions(domain);
    }),
  );

  return resolveContexts({accountId, companies, permissionsByDomain, memberships});
}
